// Dependencies
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { Form, Formik } from "formik";
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";
import { FC, useRef, useState } from "react";

// Components
import FormItem from "../../Layout/LayoutForm/FormItem";
import CategoryPicker from "~/components/Category/CategoryPicker";
import Button, { WarningButton } from "~/components/Common/Button";
import { MenuItem } from "@mui/material";
import { If } from "jsx-control-statements";
import { Switch } from "~/components/Common";

// apis
import { getEventList } from "~/services/event";
import { getCategoryList } from "~/services/category";

// Types
import { QUERY_KEY } from "~/enums";
import { TCategoryData, TTransactionData, TQueryError } from "~/types";

// Utils
import { convertDayjsUTCtoBackendUTC, handleCancelForm } from "~/utils";

// Styles
import {
  StyledUnsaved,
  StyledFormInput,
  StyledFormSelect,
  StyledFormAction,
  StyledDatePicker,
  StyledCurrencyInput,
} from "../../Layout/LayoutForm/Form.styled";
import { StyledInfoContent, StyledInfoDetail } from "../../Layout/LayoutInfo";

interface TransactionFormProps {
  onCancel: Function;
  onSubmit: Function;
  initialData?: TTransactionData;
  walletId: string;
}

const TransactionForm: FC<TransactionFormProps> = ({
  onCancel,
  onSubmit,
  initialData,
  walletId,
}) => {
  // States
  const [showCategory, setShowCategory] = useState<boolean>(false);
  const [categoryName, setCategoryName] = useState<string | undefined>(
    initialData?.categoryName
  );

  const formRef = useRef<any>(null);

  const { enqueueSnackbar } = useSnackbar();

  // Queries
  const categoryListQuery = useQuery({
    queryKey: QUERY_KEY.CATEGORY.CATEGORY_LIST,
    queryFn: () => getCategoryList(walletId),
    enabled: !!walletId,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const eventListQuery = useQuery({
    queryKey: QUERY_KEY.EVENT.EVENT_LIST,
    queryFn: () => getEventList(walletId),
    enabled: !!walletId,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const categoryData: TCategoryData[] = categoryListQuery?.data || [];

  const eventList = eventListQuery.data?.map((item) => (
    <MenuItem value={item.eventId} key={item.eventId}>
      {item.eventName}
    </MenuItem>
  ));

  // methods
  const handleShowCategoryPicker = () => {
    setShowCategory(true);
  };

  const handleHideCategoryPicker = () => {
    setShowCategory(false);
  };

  const handleSubmitForm = (values: {
    walletId: string;
    amount: string;
    categoryId: string;
    note?: string;
    transactionDate: Dayjs;
    excludeFromReport: boolean;
    eventId?: string;
    remindAt?: Dayjs;
  }) => {
    let editedValues: any = { ...values };
    if ("transactionDate" in values) {
      editedValues.transactionDate = convertDayjsUTCtoBackendUTC(
        values.transactionDate
      );
    }
    if ("remindAt" in values && values.remindAt) {
      editedValues.remindAt = convertDayjsUTCtoBackendUTC(values.remindAt);
    }

    onSubmit(editedValues);
  };

  return (
    <StyledInfoContent>
      <StyledInfoDetail>
        <Formik
          initialValues={{
            walletId,
            amount: "0",
            username: "",
            note: "",
            excludeFromReport: false,
            eventId: "",
            ...initialData,
            categoryId: initialData?.categoryId || "",
            remindAt: initialData && dayjs(initialData?.remindAt),
            transactionDate: dayjs(initialData?.transactionDate),
          }}
          // validate form values
          validationSchema={Yup.object({
            amount: Yup.number()
              .required("Amount is required")
              .moreThan(0, "Amount must be greater than 0"),
            categoryId: Yup.string().required("Category is required"),
            transactionDate: Yup.string().required(
              "Transaction date is required"
            ),
          })}
          // handle submit form
          onSubmit={handleSubmitForm}
          innerRef={formRef}
        >
          {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
            <Form>
              <FormItem
                label={"Amount"}
                htmlFor="amount"
                hasError={!!(errors.amount && touched.amount)}
                error={errors.amount}
                required
                formItem={
                  <StyledCurrencyInput
                    name={"amount"}
                    id={"amount"}
                    value={values.amount}
                    placeholder={"Enter value"}
                    decimalsLimit={2}
                    onValueChange={(value) => {
                      handleChange({ target: { name: "amount", value } });
                    }}
                  />
                }
              />

              <FormItem
                label={"Category"}
                htmlFor="categoryId"
                hasError={!!(errors.categoryId && touched.categoryId)}
                error={errors.categoryId}
                required
                formItem={
                  <>
                    <StyledFormInput
                      name={"categoryId"}
                      id={"categoryId"}
                      value={categoryName}
                      onBlur={handleBlur}
                      onFocus={handleShowCategoryPicker}
                      placeholder={"Choose category"}
                      variant={"standard"}
                      inputProps={{ autoComplete: "off" }}
                      key={1}
                    />
                    <If condition={showCategory}>
                      <CategoryPicker
                        data={categoryData}
                        onClose={handleHideCategoryPicker}
                        key={2}
                        onSelect={(
                          categoryId: string,
                          categoryName: string
                        ) => {
                          handleChange({
                            target: { name: "categoryId", value: categoryId },
                          });
                          setCategoryName(categoryName);
                          handleHideCategoryPicker();
                        }}
                      />
                    </If>
                  </>
                }
              />
              <FormItem
                label={"Note"}
                htmlFor="note"
                hasError={!!(errors.note && touched.note)}
                error={errors.note}
                formItem={
                  <StyledFormInput
                    name={"note"}
                    id={"note"}
                    value={values.note}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={"Enter note"}
                    variant={"standard"}
                  />
                }
              />
              <FormItem
                label={"Transaction date"}
                htmlFor="transactionDate"
                hasError={!!(errors.transactionDate && touched.transactionDate)}
                error={errors.transactionDate as string}
                required
                formItem={
                  <StyledDatePicker
                    value={values.transactionDate}
                    format="DD/MM/YYYY"
                    onChange={(value) => {
                      const date = value as Dayjs;
                      handleChange({
                        target: {
                          value: date,
                          name: "transactionDate",
                        },
                      });
                    }}
                  />
                }
              />
              <FormItem
                label={"Exclude From Report"}
                htmlFor="excludeFromReport"
                formItem={
                  <Switch
                    name={"excludeFromReport"}
                    id={"excludeFromReport"}
                    checked={values.excludeFromReport}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                }
              />
              <FormItem
                label={"Event"}
                htmlFor="eventId"
                hasError={!!(errors.eventId && touched.eventId)}
                error={errors.eventId}
                formItem={
                  <StyledFormSelect
                    name={"eventId"}
                    id={"eventId"}
                    labelId="eventId"
                    value={values.eventId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {eventList}
                  </StyledFormSelect>
                }
              />
              <FormItem
                label={"Remind at"}
                htmlFor="remindAt"
                hasError={!!(errors.remindAt && touched.remindAt)}
                error={errors.remindAt as string}
                formItem={
                  <StyledDatePicker
                    value={values.remindAt}
                    format="DD/MM/YYYY"
                    onChange={(value) => {
                      const date = value as Dayjs;
                      handleChange({
                        target: { value: date, name: "remindAt" },
                      });
                    }}
                  />
                }
              />
              <If condition={dirty}>
                <StyledUnsaved>Your form has unsaved fields!</StyledUnsaved>
              </If>
              <StyledFormAction>
                <WarningButton
                  onClick={() => handleCancelForm(formRef, onCancel)}
                >
                  Cancel
                </WarningButton>
                <Button type={"submit"} variant={"contained"}>
                  Save
                </Button>
              </StyledFormAction>
            </Form>
          )}
        </Formik>
      </StyledInfoDetail>
    </StyledInfoContent>
  );
};

export default TransactionForm;
